/**
 * AWARDS — the data is Wikidata's, the marks are ours.
 *
 * `awards-data.json` is built by `scripts/fetch-awards.mjs` from Wikidata, which
 * is CC0: public domain, no attribution, nothing to breach. It maps an IMDb id
 * to the prizes that title has WON (`P166` is "award received"; a nomination is
 * a different statement, so nothing here is a nominee). Each entry is an award
 * id with the win year after a colon when Wikidata knows it — `"oscar:2024"` —
 * and a bare id when it does not. The year is what lets an awards rail lead
 * with recent winners instead of opening on 1934.
 *
 * THE MARKS: official emblems now, ours as the fallback. The first pass shipped
 * only abstract glyphs of our own (laurel/wreath/star/palm) because the real
 * insignia are trademarks. The OWNER'S DECISION (2026-08-17) reverses that for
 * this app: it is private, invite-only and non-commercial, and he explicitly
 * accepted the trademark/copyright risk — so each award now carries its
 * official emblem (`emblem`, a file under /awards/ with its sources recorded
 * in web/public/awards/README.md), rendered as a tone-coloured silhouette.
 * The abstract glyphs REMAIN in AwardMark.svelte as the automatic fallback
 * whenever an asset is missing or fails to load: the badge never renders
 * broken, and reverting the decision is deleting a directory.
 */
import data from './awards-data.json';

export type AwardId =
  | 'oscar'
  | 'oscar-any'
  | 'oscar-animated'
  | 'oscar-intl'
  | 'emmy-any'
  | 'emmy-drama'
  | 'emmy-comedy'
  | 'emmy-limited'
  | 'globe'
  | 'globe-comedy'
  | 'globe-tv-drama'
  | 'globe-tv-comedy'
  | 'globe-limited'
  | 'bafta'
  | 'palme'
  | 'lion'
  | 'bear';

export interface Award {
  id: AwardId;
  /** Two or three letters, drawn inside the mark. */
  code: string;
  /** The long formal name — what it says on hover and to a screen reader. */
  label: string;
  /**
   * The name the EXPANDED badge spells out, and it says EXACTLY WHAT WAS WON
   * (the owner, 2026-08-17: "BP resolving to 'Oscar winner' is not necessarily
   * true — I want specifically what it was"). "Best Picture", not "Oscar
   * winner"; "Golden Globe — Best TV Comedy", not "Golden Globe". Never a
   * vaguer parent than the prize the id actually pins in Wikidata — the only
   * ids allowed a ceremony-wide name are the `-any` ids, because ceremony-wide
   * is what their query genuinely asks (see scripts/fetch-awards.mjs).
   */
  colloquial: string;
  /**
   * The official emblem's filename under /awards/ (see the owner's decision in
   * the header). Rendered as an alpha-mask silhouette in the award's tone;
   * sibling categories share the body's one emblem.
   */
  emblem: string;
  /**
   * The emblem's width:height, measured from the asset's viewBox (the README
   * records each). A mask has no intrinsic size, so the badge sizes its box
   * from this — a square box would render the 2.74:1 Palme frond as a sliver
   * and waste half its width on the 0.38:1 Oscar statuette.
   */
  emblemAspect: number;
  /** Our own fallback glyph: 'laurel' | 'wreath' | 'star' | 'palm'. */
  shape: 'laurel' | 'wreath' | 'star' | 'palm';
  /** The badge's own hue, as an oklch lightness-matched colour. */
  tone: string;
  /**
   * Best-first order when a title has several: the details strip lists its
   * prizes in this order, and a pooled award rail ("Emmy winners" pools four
   * emmy ids) tags each tile with the highest pool member that title won.
   */
  rank: number;
  /**
   * What kind of work usually wins this — lets a rail ask the catalogue for
   * the likely kind first instead of always trying 'movie' and falling through.
   */
  medium: 'film' | 'tv';
}

/*
 * EVERY NAME BELOW SAYS WHAT ITS WIKIDATA QUERY ACTUALLY PINS (the QIDs are in
 * scripts/fetch-awards.mjs, each verified against a known winner before it went
 * in). `oscar` is Q102427 — Best Picture, the one category — so it says "Best
 * Picture", never the vaguer "Oscar winner". The two `-any` ids are the only
 * ceremony-wide names, because ceremony-wide is what their P361 query asks:
 * `oscar-any` is a win in ANY Academy Award category (a Best Director win, a
 * Best Actress win — and a Best Picture winner carries it too, alongside
 * `oscar`), and `emmy-any` likewise for the Primetime Emmys. The festival
 * prizes (Palme, Lion, Bear) ARE their festival's one top prize, so their
 * short names are already exact.
 */
export const AWARDS: Record<AwardId, Award> = {
  oscar: { id: 'oscar', code: 'BP', label: 'Academy Award — Best Picture', colloquial: 'Best Picture', emblem: 'oscar.svg', emblemAspect: 0.38, shape: 'laurel', tone: '#e8c15a', rank: 100, medium: 'film' },
  palme: { id: 'palme', code: 'PO', label: "Palme d'Or, Cannes", colloquial: "Palme d'Or", emblem: 'palme.svg', emblemAspect: 2.74, shape: 'palm', tone: '#e0b64a', rank: 90, medium: 'film' },
  'oscar-animated': { id: 'oscar-animated', code: 'AF', label: 'Academy Award — Best Animated Feature', colloquial: 'Best Animated Feature', emblem: 'oscar.svg', emblemAspect: 0.38, shape: 'laurel', tone: '#d9bd72', rank: 88, medium: 'film' },
  'oscar-intl': { id: 'oscar-intl', code: 'IF', label: 'Academy Award — Best International Feature', colloquial: 'Best International Feature', emblem: 'oscar.svg', emblemAspect: 0.38, shape: 'laurel', tone: '#d9bd72', rank: 87, medium: 'film' },
  lion: { id: 'lion', code: 'GL', label: 'Golden Lion, Venice', colloquial: 'Golden Lion', emblem: 'lion.svg', emblemAspect: 1.64, shape: 'wreath', tone: '#d8a94b', rank: 85, medium: 'film' },
  bear: { id: 'bear', code: 'GB', label: 'Golden Bear, Berlin', colloquial: 'Golden Bear', emblem: 'bear.svg', emblemAspect: 0.65, shape: 'wreath', tone: '#c9a24d', rank: 84, medium: 'film' },
  'emmy-drama': { id: 'emmy-drama', code: 'EM', label: 'Emmy — Outstanding Drama Series', colloquial: 'Emmy — Drama Series', emblem: 'emmy.svg', emblemAspect: 0.57, shape: 'star', tone: '#9fb6e8', rank: 80, medium: 'tv' },
  'emmy-comedy': { id: 'emmy-comedy', code: 'EM', label: 'Emmy — Outstanding Comedy Series', colloquial: 'Emmy — Comedy Series', emblem: 'emmy.svg', emblemAspect: 0.57, shape: 'star', tone: '#9fb6e8', rank: 79, medium: 'tv' },
  'emmy-limited': { id: 'emmy-limited', code: 'EM', label: 'Emmy — Outstanding Limited or Anthology Series', colloquial: 'Emmy — Limited Series', emblem: 'emmy.svg', emblemAspect: 0.57, shape: 'star', tone: '#9fb6e8', rank: 78, medium: 'tv' },
  globe: { id: 'globe', code: 'GG', label: 'Golden Globe — Best Motion Picture, Drama', colloquial: 'Golden Globe — Best Drama', emblem: 'globe.svg', emblemAspect: 0.4, shape: 'star', tone: '#d9b36a', rank: 70, medium: 'film' },
  'globe-comedy': { id: 'globe-comedy', code: 'GG', label: 'Golden Globe — Best Motion Picture, Musical or Comedy', colloquial: 'Golden Globe — Best Musical or Comedy', emblem: 'globe.svg', emblemAspect: 0.4, shape: 'star', tone: '#d9b36a', rank: 69, medium: 'film' },
  'globe-tv-drama': { id: 'globe-tv-drama', code: 'GG', label: 'Golden Globe — Best Television Series, Drama', colloquial: 'Golden Globe — Best TV Drama', emblem: 'globe.svg', emblemAspect: 0.4, shape: 'star', tone: '#d9b36a', rank: 68, medium: 'tv' },
  'globe-tv-comedy': { id: 'globe-tv-comedy', code: 'GG', label: 'Golden Globe — Best Television Series, Musical or Comedy', colloquial: 'Golden Globe — Best TV Comedy', emblem: 'globe.svg', emblemAspect: 0.4, shape: 'star', tone: '#d9b36a', rank: 67, medium: 'tv' },
  'globe-limited': { id: 'globe-limited', code: 'GG', label: 'Golden Globe — Best Limited or Anthology Series or TV Film', colloquial: 'Golden Globe — Best Limited Series', emblem: 'globe.svg', emblemAspect: 0.4, shape: 'star', tone: '#d9b36a', rank: 66, medium: 'tv' },
  bafta: { id: 'bafta', code: 'BA', label: 'BAFTA — Best Film', colloquial: 'BAFTA — Best Film', emblem: 'bafta.svg', emblemAspect: 0.78, shape: 'wreath', tone: '#c8b0d8', rank: 65, medium: 'film' },
  'oscar-any': { id: 'oscar-any', code: 'AA', label: 'Academy Award (any category)', colloquial: 'Academy Award winner', emblem: 'oscar.svg', emblemAspect: 0.38, shape: 'laurel', tone: '#d9bd72', rank: 50, medium: 'film' },
  'emmy-any': { id: 'emmy-any', code: 'EM', label: 'Primetime Emmy (any category)', colloquial: 'Primetime Emmy winner', emblem: 'emmy.svg', emblemAspect: 0.57, shape: 'star', tone: '#9fb6e8', rank: 45, medium: 'tv' }
};

/*
 * The file's entries are `"oscar:2024"` or, when Wikidata has no date, bare
 * `"oscar"`. Parsed once here, into a win list per title, so nothing else in
 * the app ever sees the string encoding.
 */
const BY_TITLE = data as Record<string, string[]>;

interface Win {
  id: AwardId;
  /** The (newest) year this title won it, or null when Wikidata has no date. */
  year: number | null;
}

function parseWin(entry: string): Win {
  const i = entry.indexOf(':');
  if (i < 0) return { id: entry as AwardId, year: null };
  const year = Number(entry.slice(i + 1));
  return { id: entry.slice(0, i) as AwardId, year: Number.isInteger(year) ? year : null };
}

function winsOf(imdbId: string): Win[] {
  const raw = BY_TITLE[imdbId];
  if (!raw?.length) return [];
  return raw.map(parseWin);
}

/**
 * Every prize this title has won, best first. This is the title's FULL record,
 * and the details strip is where it shows (next to year and rating, on every
 * surface — the owner, 2026-08-17). A tile's corner never draws from it: the
 * only corner award tag is the one an award rail passes for its own prize.
 */
export function awardsFor(imdbId: string | undefined): Award[] {
  if (!imdbId) return [];
  const all = winsOf(imdbId)
    .map((w) => AWARDS[w.id])
    .filter((a): a is Award => a !== undefined)
    .sort((a, b) => b.rank - a.rank);
  /* Best Picture IS an Academy Award, so every BP winner also carries the
     any-category tag in the data (`oscar-any` sweeps all of Q19020). Showing
     both says the same thing twice — the specific one wins, the generic one
     is dropped. Only when BP is absent does "Academy Award winner" carry
     information (a win in some other category). */
  return all.some((a) => a.id === 'oscar') ? all.filter((a) => a.id !== 'oscar-any') : all;
}

/**
 * Every title that won ANY of the given prizes, NEWEST WIN FIRST — so a rail
 * built from this leads with last year's winner and trails into the classics,
 * instead of opening on the 1930s. Undated wins sort last; ties break on the
 * id so the order is stable between builds.
 */
export function titlesWithAny(ids: AwardId[]): string[] {
  const wanted = new Set<string>(ids);
  const hits: Array<{ tt: string; year: number }> = [];
  for (const tt of Object.keys(BY_TITLE)) {
    let best = -1;
    for (const w of winsOf(tt)) {
      if (!wanted.has(w.id)) continue;
      best = Math.max(best, w.year ?? 0);
    }
    if (best >= 0) hits.push({ tt, year: best });
  }
  hits.sort((a, b) => b.year - a.year || (a.tt < b.tt ? -1 : 1));
  return hits.map((h) => h.tt);
}

/** Every title that won a given prize, newest win first — one rail's source. */
export function titlesWith(id: AwardId): string[] {
  return titlesWithAny([id]);
}
